/**
 * Library-Filter-Helper (IA Befund 2, 2026-06-10).
 *
 * `FILTER_PARAM_KEYS` = die echten Filter-Dimensionen in der URL. `sort`/`dir`/
 * `view` sind PRÄSENTATION (kein Filter) und bewusst NICHT enthalten — sie
 * triggern keine „aktiv"-Affordanz und werden beim „Alle zurücksetzen" erhalten.
 *
 * Beide Helper funktionieren rein über Param-Presence, weil alle Filter-Setter
 * in LibraryPage ihren Param beim Default löschen (`setType('all')`→delete,
 * `setStatus('approved')`→delete, leere Facette/`q`/`tag`→absent) → die bloße
 * Anwesenheit eines Filter-Params bedeutet ein aktiver Filter.
 */
export const FILTER_PARAM_KEYS = [
  'q',
  'type',
  'status',
  'tag',
  'kategorie',
  'produkt',
  'verwendung',
  // Schnellzugriff-Boolean-Filter (2026-06-15): hier eingetragen → „Alle
  // zurücksetzen" (stripFilterParams) löscht sie + hasActiveFilters erkennt sie
  // (kein stuck-filter, wenn man über eine Dashboard-Triage-Karte reinkommt).
  'untagged',
  'mine',
] as const;

/** Mind. ein Filter-Param gesetzt und non-empty? (sort/dir/view ignoriert) */
export function hasActiveFilters(params: URLSearchParams): boolean {
  return FILTER_PARAM_KEYS.some((k) => (params.get(k) ?? '') !== '');
}

/**
 * Taxonomie-Facetten-Subset von `FILTER_PARAM_KEYS` (Kategorie → Produkt +
 * Verwendung). Für den Ein-Klick-„Taxonomie zurücksetzen" in der FilterBar
 * (IA-Follow-up 2026-06-10): clears nur den Browse-Facetten-Pfad, behält
 * Typ/Status/Suche/Tag (die sind keine Taxonomie). Abgegrenzt von „Alle
 * zurücksetzen" (Chip-Leiste), das alles inkl. `q` clText.
 */
export const FACET_PARAM_KEYS = ['kategorie', 'produkt', 'verwendung'] as const;

/** Mind. eine Taxonomie-Facette gesetzt? (Typ/Status/q/tag ignoriert) */
export function hasActiveFacets(params: URLSearchParams): boolean {
  return FACET_PARAM_KEYS.some((k) => (params.get(k) ?? '') !== '');
}

/**
 * Löscht nur die Taxonomie-Facetten-Params, behält alles sonstige (Typ/Status/
 * Suche/Tag + Präsentation). Gibt ein NEUES `URLSearchParams` zurück (immutable).
 */
export function stripFacetParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of FACET_PARAM_KEYS) next.delete(key);
  return next;
}

/**
 * Löscht alle Filter-Params, behält Präsentation (`view`/`sort`/`dir`) + alles
 * sonstige. Gibt ein NEUES `URLSearchParams` zurück (immutable — für den
 * `setSearchParams(prev => …)`-Callback).
 */
export function stripFilterParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of FILTER_PARAM_KEYS) next.delete(key);
  // `match` (Sprint 5, Zero-Result-OR-Fallback) ist KEIN Filter, sondern ein
  // q-gekoppelter Such-MODUS-Modifier → bewusst NICHT in FILTER_PARAM_KEYS
  // (hasActiveFilters soll einen Modus nicht als Filter-Dimension werten; `q`
  // triggert „aktiv" bereits). „Alle zurücksetzen" löscht `q`, also auch `match`.
  next.delete('match');
  return next;
}

/**
 * Facetten der Filter-Chip-Zeile (Redesign S2 Phase 2a, 2026-10-10) → die
 * URL-Params, die ein Chip-× entfernt. Ein Chip = eine Facette; „Produkt &
 * Kategorie" ist EIN Chip (ein Baum), entfernt also beide Params. Weil alle
 * Setter in LibraryPage ihren Param beim Default löschen, ist „Param weg" =
 * Default (type=all, status=approved).
 */
export type ChipFacet = 'type' | 'taxonomy' | 'verwendung' | 'tag' | 'status';

export const CHIP_FACET_PARAMS: Record<ChipFacet, readonly string[]> = {
  type: ['type'],
  taxonomy: ['kategorie', 'produkt'],
  verwendung: ['verwendung'],
  tag: ['tag'],
  status: ['status'],
};

/**
 * Entfernt NUR die Params der genannten Chip-Facette — alle anderen Filter,
 * Präsentation (sort/dir/view/cols) und Modus (match/stacks) bleiben. Gibt ein
 * NEUES `URLSearchParams` zurück (immutable).
 */
export function stripChipFacet(prev: URLSearchParams, facet: ChipFacet): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of CHIP_FACET_PARAMS[facet]) next.delete(key);
  return next;
}
